import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
  LOCATION_TYPE_OPTIONS,
  MATCH_COUNT_OPTIONS,
  LocationTypeOption,
} from "./constants";
import { Dropdown } from "./Dropdown";
import { useLocationSelector } from "./hooks/useLocationSelector";
import { CountryInput } from "../../Locations/CountryInput";
import { RegionInput } from "../../Locations/RegionInput";

interface LocationSelectorProps {
  title: string;
  showMatchSelector?: boolean;
  onClose: () => void;
  onLocationTypeChange: (locationType: LocationTypeOption) => void;
  onLocationValuesChange: (values: string[]) => void;
  onMatchTypeChange?: (matchType: string) => void;
  initialLocationType?: LocationTypeOption;
  initialLocationValues?: string[];
  initialMatchType?: string;
}

export const LocationSelector = ({
  title,
  showMatchSelector = false,
  onClose,
  onLocationTypeChange,
  onLocationValuesChange,
  onMatchTypeChange,
  initialLocationType = LocationTypeOption.COUNTRY,
  initialLocationValues = [],
  initialMatchType = "ANY",
}: LocationSelectorProps) => {
  const {
    locationType,
    locationValues,
    matchType,
    handleLocationTypeChange,
    handleMatchTypeChange,
  } = useLocationSelector({
    initialLocationType,
    initialLocationValues,
    initialMatchType,
    onLocationTypeChange,
    onLocationValuesChange,
    onMatchTypeChange,
  });

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        p: 2,
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center">
          <Typography variant="subtitle2">{title} by</Typography>
          <Box marginLeft={1}>
            <Dropdown
              options={LOCATION_TYPE_OPTIONS}
              value={locationType}
              onChange={(value) =>
                handleLocationTypeChange(value as LocationTypeOption)
              }
            />
          </Box>

          <Box marginLeft="auto">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Stack>

        {showMatchSelector && (
          <Box mt="0 !important">
            <Box component="span" display="flex" alignItems="center">
              <Typography variant="subtitle2">
                Match companies that operate in
              </Typography>
              <Dropdown
                options={MATCH_COUNT_OPTIONS}
                value={matchType}
                onChange={handleMatchTypeChange}
              />
            </Box>
            <Stack direction="row" alignItems="center">
              <Typography variant="subtitle2">
                {locationType === LocationTypeOption.COUNTRY
                  ? "of the specified countries within their hierarchy"
                  : "of the selected regions"}
              </Typography>
            </Stack>
          </Box>
        )}

        {locationType === LocationTypeOption.COUNTRY ? (
          <CountryInput
            selectedItems={locationValues}
            onCountrySelect={onLocationValuesChange}
            isMultiple={true}
          />
        ) : (
          <RegionInput
            selectedItems={locationValues}
            onRegionSelect={onLocationValuesChange}
          />
        )}
      </Stack>
    </Box>
  );
};
